import React, { useEffect, useState } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Row,
  Col,
} from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { fuelAPI, vehiclesAPI, projectsAPI } from '../../helpers/api';

interface Vehicle {
  id: string;
  plate: string;
  name?: string;
  brand?: string;
  model?: string;
}

interface Project {
  id: string;
  name: string;
  code?: string;
}

interface Site {
  id: string;
  name: string;
  code?: string;
  projectId: string;
}

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
}

interface FuelFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
}

const FUEL_TYPES = [
  { value: 'DIESEL', label: 'Dizel' },
  { value: 'GASOLINE', label: 'Benzin' },
  { value: 'LPG', label: 'LPG' },
];

const FuelFormModal: React.FC<FuelFormModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    vehicleId: '',
    quantity: '',
    totalCost: '',
    fuelType: '',
    odometer: '',
    station: '',
    customStation: '',
    filledById: '',
    invoiceNumber: '',
    projectId: '',
    siteId: '',
    notes: '',
  });

  // Data lists
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [sitePersonnel, setSitePersonnel] = useState<Personnel[]>([]);
  const [stations, setStations] = useState<string[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchData();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      vehicleId: '',
      quantity: '',
      totalCost: '',
      fuelType: '',
      odometer: '',
      station: '',
      customStation: '',
      filledById: '',
      invoiceNumber: '',
      projectId: '',
      siteId: '',
      notes: '',
    });
    setSites([]);
    setSitePersonnel([]);
  };

  const fetchData = async () => {
    try {
      const [vehiclesRes, projectsRes, stationsRes] = await Promise.all([
        vehiclesAPI.getAll({ isActive: true }),
        projectsAPI.getAll(),
        fuelAPI.getStations(),
      ]);
      setVehicles(vehiclesRes.data);
      setProjects(projectsRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      console.error('Data fetch error:', error);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    setFormData({ ...formData, projectId, siteId: '', filledById: '' });
    setSitePersonnel([]);
    if (projectId) {
      try {
        const response = await projectsAPI.getProjectSites(projectId);
        setSites(response.data);
      } catch (error) {
        console.error('Sites fetch error:', error);
        setSites([]);
      }
    } else {
      setSites([]);
    }
  };

  const handleSiteChange = async (siteId: string) => {
    setFormData({ ...formData, siteId, filledById: '' });
    if (siteId) {
      try {
        setLoadingPersonnel(true);
        const response = await projectsAPI.getSitePersonnel(siteId);
        // API returns sitePersonnel with nested personnel object
        const personnelList = response.data.map((sp: any) => sp.personnel);
        setSitePersonnel(personnelList);
      } catch (error) {
        console.error('Site personnel fetch error:', error);
        setSitePersonnel([]);
      } finally {
        setLoadingPersonnel(false);
      }
    } else {
      setSitePersonnel([]);
    }
  };

  const getActualStation = () =>
    formData.station === '__OTHER__' ? formData.customStation : formData.station;

  // Calculate unit price on the fly
  const calculatedUnitPrice =
    formData.quantity && formData.totalCost
      ? (parseFloat(formData.totalCost) / parseFloat(formData.quantity)).toFixed(2)
      : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicleId || !formData.quantity || !formData.totalCost) {
      toast.error('Araç, litre ve tutar zorunludur');
      return;
    }

    // filledById requires siteId
    if (formData.filledById && !formData.siteId) {
      toast.error('Yakıtı alan kişi seçmek için önce şantiye seçmelisiniz');
      return;
    }

    try {
      setSubmitting(true);
      const actualStation = getActualStation();

      await fuelAPI.create({
        date: formData.date,
        vehicleId: formData.vehicleId,
        quantity: parseFloat(formData.quantity),
        totalCost: parseFloat(formData.totalCost),
        fuelType: formData.fuelType || undefined,
        odometer: formData.odometer ? parseInt(formData.odometer) : undefined,
        station: actualStation || undefined,
        filledById: formData.filledById || undefined,
        invoiceNumber: formData.invoiceNumber || undefined,
        projectId: formData.projectId || undefined,
        siteId: formData.siteId || undefined,
        notes: formData.notes || undefined,
      });
      toast.success('Yakıt kaydı eklendi');
      onSuccess();
      toggle();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Yeni Yakıt Kaydı"
      size="md"
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        <Form onSubmit={handleSubmit} className="fuel-form">
          {/* Proje / Şantiye Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-building-line me-2"></i>
              Proje / Şantiye
            </div>
            <div>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Tarih *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Proje</Label>
                  <Input
                    type="select"
                    value={formData.projectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                  >
                    <option value="">Proje Seçin</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.code ? `(${p.code})` : ''}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {formData.projectId && sites.length > 0 && (
              <FormGroup>
                <Label>Şantiye</Label>
                <Input
                  type="select"
                  value={formData.siteId}
                  onChange={(e) => handleSiteChange(e.target.value)}
                >
                  <option value="">Şantiye Seçin</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.code ? `(${s.code})` : ''}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}

            {formData.siteId && (
              <FormGroup className="mb-0">
                <Label>Yakıtı Alan Kişi</Label>
                {loadingPersonnel ? (
                  <div className="text-center py-2">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : sitePersonnel.length > 0 ? (
                  <Input
                    type="select"
                    value={formData.filledById}
                    onChange={(e) =>
                      setFormData({ ...formData, filledById: e.target.value })
                    }
                  >
                    <option value="">Kişi Seçin</option>
                    {sitePersonnel.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <p className="text-muted small mb-0">
                    Bu şantiyede personel bulunmuyor
                  </p>
                )}
              </FormGroup>
            )}

            {!formData.siteId && formData.projectId && sites.length > 0 && (
              <p className="text-muted small mb-0">
                <i className="ri-information-line me-1"></i>
                Yakıtı alan kişi seçmek için önce şantiye seçin
              </p>
            )}
            </div>
          </div>

          {/* Araç ve Yakıt Bilgileri Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-gas-station-line me-2"></i>
              Araç ve Yakıt Bilgileri
            </div>
            <div>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Araç *</Label>
                  <Input
                    type="select"
                    value={formData.vehicleId}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicleId: e.target.value })
                    }
                    required
                  >
                    <option value="">Araç Seçin</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plate} {v.name ? `(${v.name})` : v.brand && v.model ? `(${v.brand} ${v.model})` : ''}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Yakıt Türü</Label>
                  <Input
                    type="select"
                    value={formData.fuelType}
                    onChange={(e) =>
                      setFormData({ ...formData, fuelType: e.target.value })
                    }
                  >
                    <option value="">Seçin</option>
                    {FUEL_TYPES.map((ft) => (
                      <option key={ft.value} value={ft.value}>
                        {ft.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Litre *</Label>
                  <Input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: e.target.value })
                    }
                    placeholder="0.00"
                    step="0.01"
                    min={0}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Tutar (₺) *</Label>
                  <Input
                    type="number"
                    value={formData.totalCost}
                    onChange={(e) =>
                      setFormData({ ...formData, totalCost: e.target.value })
                    }
                    placeholder="0.00"
                    step="0.01"
                    min={0}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Birim Fiyat</Label>
                  <Input
                    type="text"
                    value={calculatedUnitPrice ? `${calculatedUnitPrice} ₺/lt` : ''}
                    disabled
                    placeholder="Otomatik"
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Kilometre</Label>
              <Input
                type="number"
                value={formData.odometer}
                onChange={(e) =>
                  setFormData({ ...formData, odometer: e.target.value })
                }
                placeholder="Km sayacı"
                min={0}
              />
            </FormGroup>
            </div>
          </div>

          {/* İstasyon / Tedarikçi Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-store-2-line me-2"></i>
              İstasyon / Tedarikçi
            </div>
            <div>
            <FormGroup>
              <Label>İstasyon</Label>
              <Input
                type="select"
                value={formData.station}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    station: value,
                    customStation: value === '__OTHER__' ? formData.customStation : '',
                  });
                }}
              >
                <option value="">Seçin</option>
                {stations.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value="__OTHER__">+ Diğer (Yeni İstasyon)</option>
              </Input>
              {formData.station === '__OTHER__' && (
                <Input
                  type="text"
                  value={formData.customStation}
                  onChange={(e) =>
                    setFormData({ ...formData, customStation: e.target.value })
                  }
                  placeholder="Yeni istasyon adı"
                  className="mt-2"
                />
              )}
            </FormGroup>

            <FormGroup className="mb-0">
              <Label>Fiş/Fatura No</Label>
              <Input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) =>
                  setFormData({ ...formData, invoiceNumber: e.target.value })
                }
                placeholder="Belge numarası"
              />
            </FormGroup>
            </div>
          </div>

          {/* Notlar Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-file-text-line me-2"></i>
              Notlar
            </div>
            <FormGroup className="mb-0">
              <Input
                type="textarea"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Ek notlar..."
                rows={2}
              />
            </FormGroup>
          </div>

          {/* Kaydet Butonu */}
          <div className="form-actions">
          <Button color="secondary" onClick={toggle} disabled={submitting}>
            İptal
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <i className="ri-save-line me-1"></i>
                Kaydet
              </>
            )}
          </Button>
          </div>
        </Form>
      </div>
    </SideModal>
  );
};

export default FuelFormModal;
